import type { ChangeEvent } from "react";
import {
  Box, Flex, HStack, Input, SimpleGrid, Text, Textarea, useColorModeValue,
} from "@chakra-ui/react";
import { LeaderSearchInput } from "../../../../components/LeaderSearchInput";
import { ManageableSelect } from "../../../../components/ManageableSelect";

export type AdmissionField =
  | "job_title" | "job_activities" | "salary" | "meal_voucher" | "cost_center"
  | "workplace" | "work_schedule" | "work_start_time" | "work_end_time"
  | "admission_date" | "responsible_manager";

export type AdmissionDrafts = Record<AdmissionField, string>;

export const ADMISSION_FIELDS: AdmissionField[] = [
  "responsible_manager",
  "job_title", "job_activities", "salary", "meal_voucher", "cost_center",
  "workplace", "work_schedule", "work_start_time", "work_end_time",
  "admission_date",
];

type FieldDef =
  | { kind: "single"; field: AdmissionField; label: string; inputType: "text" | "date" | "textarea"; placeholder?: string; inputMode?: "text" | "decimal"; sanitize?: (v: string) => string; span?: 1 | 2 }
  | { kind: "manageableSelect"; field: AdmissionField; label: string; placeholder?: string; span?: 1 | 2 }
  | { kind: "leaderSearch"; field: AdmissionField; label: string; placeholder?: string; span?: 1 | 2 }
  | { kind: "workHours"; label: string; span?: 1 | 2 };

/** Sanitiza valor monetário (salário, vale refeição): só dígitos, um separador
 * ("," ou ".") e no máx 2 casas decimais. */
function sanitizeCurrency(v: string): string {
  const s = v.replace(/[^\d.,]/g, "");
  const sepIdx = s.search(/[.,]/);
  if (sepIdx === -1) return s;
  const sep = s[sepIdx];
  const intPart = s.slice(0, sepIdx);
  const decPart = s.slice(sepIdx + 1).replace(/[.,]/g, "").slice(0, 2);
  return `${intPart}${sep}${decPart}`;
}

const ROWS: FieldDef[] = [
  { kind: "leaderSearch",     field: "responsible_manager", label: "Gestor responsável", placeholder: "Buscar ou digitar nome do gestor..." },
  { kind: "single",           field: "job_title",      label: "Cargo",                inputType: "text",    placeholder: "Cargo da admissão" },
  { kind: "manageableSelect", field: "cost_center",   label: "Centro de custo",    placeholder: "CC do colaborador..." },
  { kind: "manageableSelect", field: "workplace",     label: "Local de trabalho",  placeholder: "Local de trabalho..." },
  { kind: "manageableSelect", field: "work_schedule", label: "Escala",            placeholder: "Escala do Trabalho..." },
  { kind: "workHours",        label: "Horário de atuação" },
  { kind: "single",           field: "salary",         label: "Salário",            inputType: "text",    placeholder: "Ex: 5000,00", inputMode: "decimal", sanitize: sanitizeCurrency },
  { kind: "single",           field: "meal_voucher",   label: "Vale Refeição",      inputType: "text",    placeholder: "Ex: 600,00", inputMode: "decimal", sanitize: sanitizeCurrency },
  { kind: "single",           field: "admission_date", label: "Data de admissão",  inputType: "date" },
  { kind: "single",           field: "job_activities", label: "Atividades do cargo", inputType: "textarea", placeholder: "Descrição das atividades do cargo...", span: 2 },
];

export interface AdmissionDataPanelProps {
  drafts: AdmissionDrafts;
  onDraftChange: (field: AdmissionField, value: string) => void;
  isSaving: boolean;
  showHeader?: boolean;
  /** Sem permissão rh.salary.view, o campo Salário fica desabilitado (o back
   * já retorna salary null). Default true p/ não quebrar usos sem a prop. */
  canViewSalary?: boolean;
}

export const AdmissionDataPanel = ({ drafts, onDraftChange, isSaving, showHeader = true, canViewSalary = true }: AdmissionDataPanelProps) => {
  const labelColor   = useColorModeValue("gray.700", "gray.200");
  const headerColor  = useColorModeValue("gray.600", "gray.400");
  const valueColor   = useColorModeValue("gray.800", "gray.100");
  const panelBg      = useColorModeValue("white", "whiteAlpha.50");
  const panelBorder  = useColorModeValue("gray.200", "whiteAlpha.100");
  const inputBg      = useColorModeValue("white", "#0a1628");
  const inputBorder  = useColorModeValue("gray.200", "whiteAlpha.200");

  const isFilled = (f: AdmissionField): boolean => {
    if (f === "salary" && !canViewSalary) return false; // não marca pendência sem acesso
    const v = drafts[f];
    return !!v && v.trim() !== "";
  };

  const renderField = (row: FieldDef) => {
    if (row.kind === "single") {
      const cfg = row;
      const v = drafts[cfg.field];
      // Sem permissão, o campo Salário fica bloqueado (não é possível ler/editar).
      const disabled = isSaving || (cfg.field === "salary" && !canViewSalary);
      const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const raw = e.target.value;
        onDraftChange(cfg.field, cfg.sanitize ? cfg.sanitize(raw) : raw);
      };
      if (cfg.inputType === "textarea") {
        return (
          <Textarea
            value={v} onChange={handleChange}
            placeholder={cfg.placeholder} bg={inputBg} borderColor={inputBorder} size="sm" rows={3}
            borderRadius="md" isDisabled={disabled}
          />
        );
      }
      return (
        <Input
          type={cfg.inputType} value={v} onChange={handleChange}
          placeholder={cfg.placeholder} bg={inputBg} borderColor={inputBorder} size="sm" borderRadius="md"
          isDisabled={disabled}
          inputMode={cfg.inputMode ?? "text"}
        />
      );
    }
    if (row.kind === "manageableSelect") {
      return (
        <ManageableSelect
          fieldKey={row.field}
          value={drafts[row.field]}
          onChange={(v) => onDraftChange(row.field, v)}
          placeholder={row.placeholder}
          isDisabled={isSaving}
        />
      );
    }
    if (row.kind === "leaderSearch") {
      return (
        <LeaderSearchInput
          value={drafts[row.field]}
          onChange={(v) => onDraftChange(row.field, v)}
          placeholder={row.placeholder}
          size="sm"
          bg={inputBg}
          isDisabled={isSaving}
        />
      );
    }
    // workHours
    return (
      <HStack spacing={2}>
        <Input
          type="time" value={drafts.work_start_time}
          onChange={(e) => onDraftChange("work_start_time", e.target.value)}
          bg={inputBg} borderColor={inputBorder} size="sm" borderRadius="md" w="110px"
          isDisabled={isSaving}
        />
        <Text fontSize="sm" color={valueColor} flexShrink={0}>às</Text>
        <Input
          type="time" value={drafts.work_end_time}
          onChange={(e) => onDraftChange("work_end_time", e.target.value)}
          bg={inputBg} borderColor={inputBorder} size="sm" borderRadius="md" w="110px"
          isDisabled={isSaving}
        />
      </HStack>
    );
  };

  return (
    <Box>
      {showHeader && (
        <Flex align="center" mb={2}>
          <Text fontSize="xs" fontWeight="bold" color={headerColor} letterSpacing="wider" textTransform="uppercase">
            Dados da admissão
          </Text>
        </Flex>
      )}

      <Box bg={panelBg} borderWidth="1px" borderColor={panelBorder} borderRadius="md" px={4} py={3}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} spacingY={3}>
          {ROWS.map((row) => {
            const filled =
              row.kind === "workHours"
                ? isFilled("work_start_time") && isFilled("work_end_time")
                : isFilled(row.field);
            return (
              <Box key={row.kind === "workHours" ? "workHours" : row.field} gridColumn={row.span === 2 ? "span 2" : undefined}>
                <HStack mb={1.5} spacing={2}>
                  <Box
                    w={2} h={2} borderRadius="full" flexShrink={0}
                    bg={filled ? "green.400" : "orange.400"}
                  />
                  <Text fontSize="sm" fontWeight="medium" color={labelColor}>
                    {row.label}
                  </Text>
                </HStack>
                {renderField(row)}
              </Box>
            );
          })}
        </SimpleGrid>
      </Box>
    </Box>
  );
};